import { useState, useCallback } from 'react';
import { useColorContext } from '../contexts/ColorContext';
import { useColorFavorites } from './useColorFavorites';
import { colorEngine } from '../lib/colorEngine';

/**
 * Descriptor for a color being acted upon.
 * At minimum a hex string is required; colorspace and original format
 * help preserve the user's preferred representation.
 */
export interface ColorDescriptor {
  hex: string;
  colorspace?: string;
  originalFormat?: string;
}

export interface UseColorActionsResult {
  /** Set the given color as the active color in the global ColorContext */
  setActiveColor: (descriptor: ColorDescriptor) => void;

  /** Copy a hex string (or arbitrary field value) to the clipboard with inline feedback */
  copyColor: (value: string, field?: string) => void;

  /** Save a hex color to the favorites database */
  saveToFavorites: (hex: string) => Promise<void>;

  /** Whether the last copy action is still showing feedback */
  copiedField: string | null;
}

/**
 * Shared action helper that any swatch / color value can invoke.
 *
 * Provides three universal actions:
 *  1. setActiveColor – sets the global active color
 *  2. copyColor      – clipboard copy with feedback state
 *  3. saveToFavorites – persists to IndexedDB favorites
 */
export function useColorActions(): UseColorActionsResult {
  const { setInput, copyToClipboard: ctxCopy } = useColorContext();
  const { addFavorite } = useColorFavorites();

  const [copiedField, setCopiedField] = useState<string | null>(null);

  // ── setActiveColor ──────────────────────────────────────────────────
  const setActiveColor = useCallback(
    (descriptor: ColorDescriptor) => {
      // Prefer the original format string if available (e.g. "hsl(217, 91%, 60%)")
      // otherwise fall back to the raw hex value.
      const value = descriptor.originalFormat?.trim() || descriptor.hex;

      // When only hex is provided, infer the best display format via colorEngine
      // so the ContextBar input shows a meaningful string.
      if (!descriptor.originalFormat && descriptor.hex) {
        // Try parsing to see if colorEngine can detect a richer representation
        const parsed = colorEngine.parseColorWithFeedback(descriptor.hex);
        if (parsed.success && parsed.result) {
          setInput(value);
          return;
        }
      }

      setInput(value);
    },
    [setInput],
  );

  // ── copyColor ───────────────────────────────────────────────────────
  const copyColor = useCallback(
    (value: string, field?: string) => {
      const fieldKey = field ?? 'copy-action';
      ctxCopy(value, fieldKey);
      setCopiedField(fieldKey);
      setTimeout(() => setCopiedField(null), 2000);
    },
    [ctxCopy],
  );

  // ── saveToFavorites ─────────────────────────────────────────────────
  const saveToFavorites = useCallback(
    async (hex: string) => {
      await addFavorite(hex);
    },
    [addFavorite],
  );

  return { setActiveColor, copyColor, saveToFavorites, copiedField };
}
